import React, { useState, ReactNode, useEffect, useMemo, useRef } from "react";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, IconButton, TextField, Tabs, Tab } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import Select from "react-select";
import { ResolutionKeys, StateToShow, UserDefinedDataset, WaterTye, HistogramDataType, ComparitorData, MetadataRespone, BuilderToCompareData, CustomMetricData, StorageCustomMetricData, AttributeResponse } from '../types';
import DynamicSelector from '../components/DynamicSelector';
import YearSelectSlider from '../components/Slider';
import ParametersSelected from '../components/UserDefinedMetricParameters';
import BarHistogram from './UserDefinedBarChart';
import CategoryAssignment from './CategoryAssignment';
import { handle_attribute_change, handle_dataset_change, handle_page_load, handle_resolution_or_year_or_month_change, handle_compare_histogram, handle_compare_check } from '../functions/backend_handlers';
import Alert from '@mui/material/Alert';
import { convertToMap, createComparitorDataMessage } from "../functions/miscellaneous";
import MonthSelectSlider from "./SliderMonth";

interface UserDefinedMetricProps {
  appendToDatalistAndSetToValue : (new_data : CustomMetricData) => void;
  incoming_comparitor_data  : CustomMetricData | undefined;
}

interface DatasetOption {
  value: string;
  label: string;
  category: string;
}

const UserDefinedMetric: React.FC<UserDefinedMetricProps> = ({ 
  appendToDatalistAndSetToValue,
  incoming_comparitor_data
}) => {

  const inputRefs = useRef<{[key: number]: HTMLInputElement | null}>({});

  const [currentTab, setCurrentTab] = useState(0);
  const [portalContainer, setPortalContainer] = useState(null);
  const [open, setOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string | null>(null);

  // User defined datasets
  const [available_user_datasets, setAvailableUserDatasets] = useState<UserDefinedDataset>({});
  const [current_user_dataset, setCurrentUserDataset] = useState<string | undefined>(undefined);
  const [current_user_polygon_values, setCurrentUserPolygonValues] = useState<{ [key: string]: string; }>({});
  const [is_current_user_defined, setIsCurrentUserDefined] = useState<boolean>(false);

  // Main states
  const [current_dataset, setCurrentDataset] = useState<string | undefined>(undefined);
  const [current_attribute, setCurrentAttribute] = useState<string | undefined>(undefined);
  const [current_year, setCurrentYear] = useState<number | undefined>(0);
  const [current_month, setCurrentMonth] = useState<number | undefined>(0);
  const [modal_current_parameters, setCurrentParameters] = useState<StateToShow>({})
  const [current_water_type, setCurrentWaterType] = useState<WaterTye>(undefined);
  const [current_variable_name, setCurrentVariableName] = useState<string>("");
  const [custom_metric_name, setCustomMetricName] = useState<string>("");

  // Secondary states
  const [has_time, setHasTime] = useState<boolean>(false);
  const [valid_years, setValidYears] = useState<number[]>([]);
  const [valid_months, setValidMonths] = useState<number[]>([]);
  const [attributes_list, setAttributesList] = useState<{ names: string[]; titles: string[] }>({ names: [], titles: [] });
  const [available_datasets, setAvailableDatasets] = useState<{ categories: string[], names: string[]; titles: string[] }>({categories:[], names: [], titles: [] });
  const [polygon_values, setPolygonValues] = useState<{ [key: string]: string; }>({});
  const [polygon_type, setPolygonType] = useState<"Continuous" | "Discrete">("Continuous");
  const [available_water_types, setAvailableWaterTypes] = useState<WaterTye[]>([undefined]);
  const [current_dataset_level, setCurrentDatasetLevel] = useState<"1" | "2" | "3" | null>(null);
  const [attribute_categories, setAttributeCategories] = useState<{ [key: string]: number }>({});

  const [data_is_missing, setDataIsMissing] = useState(false);
  
  // Pretty text for main states
  const [current_title_dataset, setCurrentTitleDataset] = useState<string>("");
  const [current_title_attribute, setCurrentTitleAttribute] = useState<string>("");

   // For user datasets, to call 'calculate properly'
   const [current_resolution_filter, setCurrentResolutionFilter] = useState<ResolutionKeys>("state");
  
  // Comparator specific
  const [ComparitorDatas, setComparitorDatas] = useState<ComparitorData>({
    variables: {},
    equation_fresh_surface_water: "",
    equation_fresh_ground_water: "",
    equation_brackish_ground_water: "",
    resolution: "state", // just some resolution, only used if has_user_defined is true
    has_user_defined : false,
  });
  
  const [equations, setEquations] = useState(["", "", ""]);
  const [input_values, setInputValues] = useState<(number | undefined)[] | number | string[] | undefined>(undefined);

  const [histogramData, setHistogramData] = useState<HistogramDataType[]>([
    { bins: [0, 1], min: 0, max: 1, binWidth: 1, type: "Continuous" }, // Histogram 0
    { bins: [2, 3, 4], min: 0, max: 1, binWidth: 1, type: "Continuous" }, // Histogram 1
    { bins: [5, 6, 7, 8], min: 0, max: 1, binWidth: 1, type: "Continuous" } // Histogram 2
  ]);

  const [sliderValues, setSliderValues] = useState<[[number, number],[number, number],[number, number]]>([
    [-1,-3],  // First histogram
    [-1,-3],  // Second histogram
    [-1,-3]   // Third histogram
  ]);

  const [bucketValues, setBucketValues] = useState<{
     "fgw": [string[],string[],string[]],
     "bgw": [string[],string[],string[]],
     "fsw": [string[],string[],string[]]
  }>({
    "fgw": [[],[],[]],
    "bgw": [[],[],[]],
    "fsw": [[],[],[]]
  });

  const [isFlipped, setIsFlipped] = useState([false, false, false]);  // One state per slider
  const [loadedIndices, setLoadedIndices] = useState<number[]>([]);
  const [errorIndices, setErrorIndices] = useState<number | null>(null);

  const updateMetadata = (metadata: MetadataRespone) => {
    setAttributesList({ names: metadata.Data.available_attributes, titles: metadata.Data.available_attribute_titles });
    setValidYears(metadata.Data.available_years.map(value => Number(value)));
    setValidMonths(metadata.Data.available_months.map(value => Number(value)));
    setHasTime(metadata.Data.has_time);
    setCurrentDataset(metadata.Data.dataset_name);
    setCurrentYear(Number(metadata.Data.available_years[0]));
    setCurrentMonth(Number(metadata.Data.available_months[0]));
    setAvailableWaterTypes(metadata.Data.available_water_types);
    setCurrentDatasetLevel(metadata.Data.dataset_level);
    setCurrentUserDataset(undefined);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    // If trying to go to Process Data tab without any parameters
    if (newValue === 1 && Object.keys(ComparitorDatas.variables).length === 0) {
      setAlertMessage("Please add at least one parameter before proceeding");
      return;
    }
    
    setAlertMessage(null);
    setCurrentTab(newValue);
  };

  const handleDataSetChangeOnDropdown = (option: DatasetOption | null) => {
    if(option){
      if(option.category === "Custom Datasets"){ // user defined
        setShowUserDataset(available_user_datasets, option.value);
      } else {
        setCurrentTitleDataset(option.label);
        handleDatasetChange(option.value);
        setIsCurrentUserDefined(false);
      }
    }
  };

  const handleAttributeChangeOnDropdown = (option: any) => {
    setCurrentTitleAttribute(option.label);
    handleAttributeChange(option.value);
  };

  const updateAttribute = (attribute : AttributeResponse) => {
    setPolygonType(attribute.Data.polygon_type);
    setAttributeCategories(attribute.Data.categories);
    setCurrentAttribute(attribute.Data.attribute_name);
    setCurrentWaterType(attribute.Data.water_type);
    setCurrentUserPolygonValues({});
    setIsCurrentUserDefined(false);
  };

  const handleDatasetChange = async (dataset: string) => {
    setCurrentDataset(dataset);
    const { metadata, current_parameters } = await handle_dataset_change(dataset, "state", current_water_type);
    setCurrentParameters(current_parameters);
    updateMetadata(metadata);
    if (current_attribute) setCurrentAttribute(undefined);
  };

  const handleAttributeChange = async (attribute: string) => {
    if (current_dataset) {
      setCurrentAttribute(attribute);
      const { metadata, attribute: attrData, current_parameters } = await handle_attribute_change(current_dataset, attribute, "state", current_water_type);
      
      setCurrentParameters(current_parameters);
      updateMetadata(metadata);
      
      if (attrData) {
        updateAttribute(attrData);
        setDataIsMissing(false);
      } else {
        setDataIsMissing(true);
      }
    }
  };

  const setShowUserDataset = (user_data: UserDefinedDataset, current_user_data: string | undefined) => {
    if (current_user_data) {
      setCurrentUserPolygonValues(user_data[current_user_data].polygon_values);
      setPolygonType(user_data[current_user_data].polygon_type);
      setAttributeCategories(user_data[current_user_data].categories);
      setCurrentUserDataset(current_user_data);
      setIsCurrentUserDefined(true);
      setHasTime(false);
      setCurrentAttribute(undefined);
      setCurrentDataset(undefined);
      setInputValues([]);
    }
  };

  const handleDynamicSelectorChange = async (key: string, value: string) => {
    const updatedParameters = { ...modal_current_parameters };
    if (updatedParameters[key]) {
      updatedParameters[key].current_code = value;
    }
    if (current_dataset && current_attribute) {
      const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, updatedParameters, "state", current_year, current_month, current_water_type,"False");
      setCurrentParameters(updatedParameters);
      if (attribute) {
        updateAttribute(attribute);
        setDataIsMissing(false);
      } else {
        setDataIsMissing(true);
      }
    }
  };

  const handleResolutionOrYearOrMonthChange = async ({ resolution, year, month }: { resolution?: ResolutionKeys; year?: number; month?: number }) => {
    if (year) setCurrentYear(Number(year));
    else year = current_year;

    if (month) setCurrentMonth(Number(month));
    else month = current_month;

    if (current_dataset && current_attribute) {
      const attribute = await handle_resolution_or_year_or_month_change(current_dataset, current_attribute, modal_current_parameters, "state", year, month, current_water_type,"False");
      setCurrentParameters(modal_current_parameters);

      if (attribute) {
        updateAttribute(attribute);
        setDataIsMissing(false);
      } else {
        setDataIsMissing(true);
      }
    }
  };

  const handleYearSliderChange = (event: Event, newValue: number | number[]) => {
    handleResolutionOrYearOrMonthChange({ year: valid_years[newValue as number] });
  };

  const handleMonthSliderChange = (event: Event, newValue: number | number[]) => {
    // newValue is the index of the selected year
    handleResolutionOrYearOrMonthChange({ month: valid_months[newValue as number] });
};

  const handleTextChange = (newText: string) => {
    setCurrentVariableName(newText);
  };

  const addNewParameter = () => {    
    const regex = /^[A-Za-z_][A-Za-z0-9_]*$/;
    const re = new RegExp(regex);


    if (!current_variable_name) {
      setAlertMessage("Please enter a variable name");
      return;
    }

    if (!re.test(current_variable_name)) {
      setAlertMessage("Please enter a valid variable name, should only contain letters, numbers and '_'");
      return;
    }
    
    let newLine : BuilderToCompareData = {
      attribute_name: "",
      dataset: "",
      year: undefined,
      month: undefined,
      parameter: {},
      type: "Continuous",
      attribute_title: "",
      dataset_title: "",
      water_type: undefined,
      is_user_defined: false,
      polygon_values: {}
    };

    if(is_current_user_defined){
      newLine = {
      'attribute_name' : '', 
      'dataset' : current_user_dataset || "", 
      "year": undefined, 
      "month": undefined,
      "parameter": {},
      "type":  polygon_type,
      "attribute_title": "",
      "dataset_title" : current_user_dataset || "",
      "water_type" : undefined,
      "is_user_defined" : true,
      "polygon_values" : current_user_polygon_values
      };
      
    } else {
      newLine = {
        'attribute_name': current_attribute || "",
        'dataset': current_dataset || "",
        'year': current_year,
        'month': current_month,
        'parameter': modal_current_parameters,
        'type': polygon_type,
        'attribute_title': current_title_attribute,
        'dataset_title': current_title_dataset,
        'water_type': undefined,
        'is_user_defined': false,
        'polygon_values': {}
      };
    }
    
    setComparitorDatas(prevData => {
      const newVariables = { ...prevData.variables };
      newVariables[current_variable_name] = newLine;
      return { ...prevData, variables: newVariables };
    });
    
    // Reset variable name and clear alert
    setCurrentVariableName("");
    setAlertMessage(null);
  };

  const handleSliderChange = (index: number, newValue: number[]) => {
    if (newValue.length === 2) {
      setSliderValues(prev => {
        const newSliders : [[number, number],[number, number],[number, number]] = [...prev];
        newSliders[index] = [newValue[0], newValue[1]];
        return newSliders;
      });
    }
  };

  const handleReverseCategories = (index: number) => {
    setIsFlipped(prev => {
      const newFlipped = [...prev];
      newFlipped[index] = !newFlipped[index];
      return newFlipped;
    });
  };

  const handleEquationChange = (index: number, value: string) => {
    setEquations((prev) => {
      const updated = [...prev];
      updated[index] = value;
      return updated;
    });

    setComparitorDatas((prev) => {
      return {
        ...prev,
        equation_fresh_surface_water: index === 0 ? value : prev.equation_fresh_surface_water,
        equation_fresh_ground_water: index === 1 ? value : prev.equation_fresh_ground_water,
        equation_brackish_ground_water: index === 2 ? value : prev.equation_brackish_ground_water,
      };
    });
  };

  const handleProcessData = async (index: number, set_slider : boolean, comparitor_data?: ComparitorData, current_equations?:string[]) => {
    if(!comparitor_data) comparitor_data = ComparitorDatas;
    if(!current_equations) current_equations = equations;

    try {
      if (Object.keys(comparitor_data.variables).length === 0) {
        setErrorIndices(index);
        setAlertMessage("Please add at least one parameter before processing data");
        return;
      }
      
      if (!current_equations[index]) {
        setErrorIndices(index);
        setAlertMessage("Please enter an equation before processing data");
        return;
      }

      let messageData = createComparitorDataMessage(comparitor_data, current_resolution_filter, index);
      const {type, data: resp_comp_hist } = await handle_compare_histogram(messageData);

      if(type == "severe_error"){
        setErrorIndices(index);
        setAlertMessage(resp_comp_hist);
        return;
      }

      if(type == "error"){
        setErrorIndices(index);
        setAlertMessage(`${resp_comp_hist.error}: ${resp_comp_hist.message}`);
        return;
      }

      setHistogramData((prev) => {
        const updated = [...prev];
        updated[index] = resp_comp_hist.histogram;
        setErrorIndices(null);
        return updated;
      });
      
      setLoadedIndices((prev) => {
        if (!prev.includes(index)) {
          return [...prev, index];
        }
        return prev;
      });

      if (resp_comp_hist.histogram.type === "Continuous" && set_slider) {
        const minSliderValue = (resp_comp_hist.histogram.max - resp_comp_hist.histogram.min) * 0.33 + resp_comp_hist.histogram.min;
        const maxSliderValue = (resp_comp_hist.histogram.max - resp_comp_hist.histogram.min) * 0.66 + resp_comp_hist.histogram.min;

        handleSliderChange(index, [minSliderValue, maxSliderValue]);
      }
      
      setAlertMessage(null);
    } catch (error) {
      console.error("Error processing data:", error);
      setErrorIndices(index);
      setAlertMessage("An error occurred while processing data");
    }
  };

  const updateCustomMetadataonSave = async () => {
    if (!custom_metric_name.trim()) {
      setAlertMessage("Please enter a unique metric name");
      return;
    }
    
    if (Object.keys(ComparitorDatas.variables).length === 0) {
      setAlertMessage("Please add at least one parameter before saving");
      return;
    }
    
    if (loadedIndices.length === 0) {
      setAlertMessage("Please process at least one equation before saving");
      return;
    }
    
    try {
      // Get calculated values for each water type
      let messageData1 = createComparitorDataMessage(ComparitorDatas, current_resolution_filter, 0);
      if (histogramData[0].type == "Continuous") {
        if(sliderValues[0] != null)
        messageData1 = { ...messageData1, map_value: {type: "Continuous", is_flipped: isFlipped[0], map: [sliderValues[0][0],sliderValues[0][1]]}};
      } else {
        messageData1 = { ...messageData1, map_value: {type: "Discrete", map: convertToMap(bucketValues,"fsw") }};
      }

      let messageData2 = createComparitorDataMessage(ComparitorDatas, current_resolution_filter, 1);
      if (histogramData[1].type == "Continuous") {
        if(sliderValues[1] != null)
        messageData2 = { ...messageData2, map_value: {type: "Continuous", is_flipped: isFlipped[1], map: [sliderValues[1][0],sliderValues[1][1]]}};
      } else {
        messageData2 = { ...messageData2, map_value: {type: "Discrete", map: convertToMap(bucketValues,"bgw") }};
      }

      let messageData3 = createComparitorDataMessage(ComparitorDatas, current_resolution_filter, 2);
      if (histogramData[2].type == "Continuous") {
        if(sliderValues[2] != null)
        messageData3 = { ...messageData3, map_value: {type: "Continuous", is_flipped: isFlipped[2], map: [sliderValues[2][0],sliderValues[2][1]]}};
      } else {
        messageData3 = { ...messageData3, map_value: {type: "Discrete", map: convertToMap(bucketValues,"fgw") }};
      }
      
      const resp_comp_calc1 = await handle_compare_check(messageData1);
      const resp_comp_calc2 = await handle_compare_check(messageData2);
      const resp_comp_calc3 = await handle_compare_check(messageData3);

      // Store custom metric in session storage
      let customAttrs = [false, false, false];
      let to_check_variables : string[]  = [];
      if ("extracted_vars" in resp_comp_calc1) { 
        customAttrs[0] = true; 
        to_check_variables = to_check_variables.concat(resp_comp_calc1.extracted_vars);
      }
      if ("extracted_vars" in resp_comp_calc2) { 
        customAttrs[1] = true; 
        to_check_variables = to_check_variables.concat(resp_comp_calc2.extracted_vars);
      }
      if ("extracted_vars" in resp_comp_calc3) { 
        customAttrs[2] = true; 
        to_check_variables = to_check_variables.concat(resp_comp_calc3.extracted_vars);
      }

      let comparitor_data = ComparitorDatas
      let user_data_was_used = false;
      for(const used_var of to_check_variables){
        if(ComparitorDatas.variables[used_var].is_user_defined){
          //set available resolutions, bc user data was used
          user_data_was_used = true;
          setComparitorDatas(prevData => {
            comparitor_data = { ...prevData, 
              resolution:  available_user_datasets[ComparitorDatas.variables[used_var].dataset].resolution, 
              has_user_defined: true
            }
            return comparitor_data;
          }
          );
          break;
        }
      }
      if(!user_data_was_used) setComparitorDatas(prevData => {comparitor_data = { ...prevData, has_user_defined: false}; return comparitor_data}); 

      // Define the object to store
      const metricData : CustomMetricData = {
        name: custom_metric_name,
        titles: custom_metric_name,
        sliderValues: sliderValues,
        isFlipped: isFlipped,
        buckets: bucketValues,
        custom_attributes: customAttrs,
        polygon_type: [histogramData[0].type, histogramData[1].type, histogramData[2].type],
        comparitor_data : comparitor_data,
      };

      // Retrieve existing custom metrics from sessionStorage
      const storedData = localStorage.getItem("custom_metrics");
      let customMetrics : StorageCustomMetricData = storedData ? JSON.parse(storedData) : {};

      // Add or update the custom metric
      customMetrics[custom_metric_name] = metricData;

      // Save back to sessionStorage
      localStorage.setItem("custom_metrics", JSON.stringify(customMetrics));

      appendToDatalistAndSetToValue(metricData);

      setOpen(false);
    } catch (error) {
      console.error("Error saving metric:", error);
      setAlertMessage("Error saving metric. Please check your equations and try again.");
    }
  };

  const handleCustomNameChange = (custom_name_string: string): void => {
    const modded_name = custom_name_string;
    setCustomMetricName(modded_name);
  };

  // 'PAGE' LOAD
  useEffect(() => {
    const storedDatasetsString = localStorage.getItem("user_datasets");
    const storedDatasets = storedDatasetsString ? JSON.parse(storedDatasetsString) : {};
    setAvailableUserDatasets(storedDatasets);

    handle_page_load(false).then(({ metadata, attribute, current_parameters, datalist }) => {
      // Append the keys from storedDatasets to datalist
      Object.keys(storedDatasets).forEach(key => {
        datalist.names.push(key);
        datalist.titles.push(key); // Using the same key as the title
        datalist.categories.push("Custom Datasets"); // Fixed category for all
      });
      setAvailableDatasets(datalist);
    }).catch((error: any) => console.error("Error loading data:", error));
  }, []);

  useEffect(() => {
    if(data_is_missing){
      setAlertMessage("There are no data available for these inputs!");
    } else {
      setAlertMessage(null);
    }
  }, [data_is_missing]);


  //filter user defined data if we added new variable
  useEffect(() => {
    for (const for_variable_name in ComparitorDatas.variables) {
        if(ComparitorDatas.variables[for_variable_name].is_user_defined){
            //filter data
            let datalist = filterCustomDatasets(available_datasets);

            const used_user_data = ComparitorDatas.variables[for_variable_name].dataset

            setCurrentResolutionFilter(available_user_datasets[used_user_data].resolution)

            // Append the keys from storedDatasets to datalist
            Object.keys(available_user_datasets).forEach(key => {
                if(available_user_datasets[key].resolution == available_user_datasets[used_user_data].resolution){
                    datalist.names.push(key);
                    datalist.titles.push(key); // Using the same key as the title
                    datalist.categories.push("Custom Datasets"); // Fixed category for all
                }
            });

            setAvailableDatasets(datalist);

           // setCurrentAvailableResolitions([current_resolution]);
            return
        }
    }
    //set all data
    let datalist = filterCustomDatasets(available_datasets);

    // Append the keys from storedDatasets to datalist
    Object.keys(available_user_datasets).forEach(key => {
        datalist.names.push(key);
        datalist.titles.push(key); // Using the same key as the title
        datalist.categories.push("Custom Datasets"); // Fixed category for all
    });

    setCurrentResolutionFilter('state');

    setAvailableDatasets(datalist);
  }, [ComparitorDatas]);

  useMemo(() => {
    //0 equation_fresh_surface_water
    //1 equation_fresh_ground_water
    //2 equation_brackish_ground_water
    if(incoming_comparitor_data){

      setLoadedIndices([]);

      setBucketValues(incoming_comparitor_data.buckets)

      setComparitorDatas(incoming_comparitor_data.comparitor_data)
      
      setIsFlipped(incoming_comparitor_data.isFlipped)
      
      setSliderValues(incoming_comparitor_data.sliderValues)

      setCurrentResolutionFilter(incoming_comparitor_data.comparitor_data.resolution);

      const current_equations = [ 
        incoming_comparitor_data.comparitor_data.equation_fresh_surface_water,
        incoming_comparitor_data.comparitor_data.equation_fresh_ground_water,
        incoming_comparitor_data.comparitor_data.equation_brackish_ground_water
      ]

      setEquations(current_equations)

      //do proccess data for each true in incoming_comparitor_data.custom_attributes
      for(let index=0; index < incoming_comparitor_data.custom_attributes.length; index++){
        if(incoming_comparitor_data.custom_attributes[index]){
          handleProcessData(index, false, incoming_comparitor_data.comparitor_data, current_equations);
        }
      }
      
      setCustomMetricName(incoming_comparitor_data.name);

    } else {

      setLoadedIndices([]);

      setBucketValues(
        {
          "fgw": [[],[],[]],
          "bgw": [[],[],[]],
          "fsw": [[],[],[]]
        }
      )

      setComparitorDatas(
        {
          variables: {},
          equation_fresh_surface_water: "",
          equation_fresh_ground_water: "",
          equation_brackish_ground_water: "",
          resolution: "state", // just some resolution, only used if has_user_defined is true
          has_user_defined : false,
        }
      )
      
      setIsFlipped([false, false, false])
      
      setSliderValues(
        [
          [-1,-3],  // First histogram
          [-1,-3],  // Second histogram
          [-1,-3]   // Third histogram
        ]
      )

      const current_equations = [ "", "", ""];
      setEquations(current_equations)

      setCurrentResolutionFilter("state");

      setLoadedIndices([]);
    }
  }, [incoming_comparitor_data])


  // Function to filter out "Custom Datasets" and corresponding entries
  const filterCustomDatasets = (data:  { categories: string[]; names: string[]; titles: string[];} ):  { categories: string[]; names: string[]; titles: string[];} => {
    // Create result arrays
    const filteredCategories: string[] = [];
    const filteredNames: string[] = [];
    const filteredTitles: string[] = [];
    
    // Loop through all entries and only keep non-"Custom Datasets" ones
    for (let i = 0; i < data.categories.length; i++) {
    if (data.categories[i] !== "Custom Datasets") {
        filteredCategories.push(data.categories[i]);
        filteredNames.push(data.names[i]);
        filteredTitles.push(data.titles[i]);
    }
    }
    
    return {
    categories: filteredCategories,
    names: filteredNames,
    titles: filteredTitles
    };
};

  // Format options for the Dataset dropdown
  const formatDatasetOptions = () => {
    return available_datasets.names.map((name, i) => ({
      value: name,
      label: available_datasets.titles[i],
      category: available_datasets.categories[i]
    }));
  };

  // Group options by category
  const groupedOptions = React.useMemo(() => {
    const allOptions = formatDatasetOptions();
    const groups: Record<string, DatasetOption[]> = {};
    
    allOptions.forEach(option => {
      if (!groups[option.category]) {
        groups[option.category] = [];
      }
      groups[option.category].push(option);
    });
    
    return Object.entries(groups).map(([label, options]) => ({
      label,
      options
    }));
  }, [available_datasets]);

  // Get the selected dataset option
  const selectedDatasetOption = current_dataset ? {
    value: current_dataset,
    label: available_datasets.titles[available_datasets.names.indexOf(current_dataset)],
    category: available_datasets.categories[available_datasets.names.indexOf(current_dataset)]
  } : (current_user_dataset ? {
    value: current_user_dataset,
    label: current_user_dataset,
    category: "Custom Datasets"
  } : null);

  // Handle open/close
  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    setCurrentTab(0); // Reset to first tab when closing
    setAlertMessage(null); // Clear any alerts
    // Other state could be reset here if needed
  };

  interface IsolatedParameterProps {
    children: ReactNode;
  }

  const IsolatedParameter: React.FC<IsolatedParameterProps> = ({ children }) => {
    return (
      <div 
        style={{ position: 'relative' }}
        onClick={e => e.stopPropagation()}
        onMouseDown={e => e.stopPropagation()}
        onMouseUp={e => e.stopPropagation()}
        onKeyDown={e => e.stopPropagation()}
        onKeyUp={e => e.stopPropagation()}
        onFocus={e => e.stopPropagation()}
      >
        {children}
      </div>
    );
  };

  const dialogStyle = {
    width: '60%',
    maxWidth: '60%',
    height: '60%',
    maxHeight: '60%',
    margin: '0 auto',
  };
  
  const dialogContentStyle = {
    height: 'calc(100% - 120px)', // Account for header and footer
    overflow: 'auto',
  };

  const processDataCardStyle = {
    flex: '1 1 280px',
    minWidth: '260px', // Ensure cards don't get too narrow
    maxWidth: '100%',  // Allow full width in small screens
    border: '1px solid #e0e0e0',
    borderRadius: '4px',
    padding: '15px',
    backgroundColor: '#fbfbfb',
    marginBottom: '15px' // Space between cards when they stack
  };

  return (
    <>
      <button 
        className="open-popup-button" 
        onClick={handleOpen}
        style={{
          backgroundColor: '#1976d2',
          color: 'white',
          border: 'none',
          borderRadius: '4px',
          padding: '8px 16px',
          cursor: 'pointer',
          fontSize: '14px',
          fontWeight: '500',
          width: "100%",
        }}
      >
        Customize Metric
      </button>
      
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={false}
        sx={{
          
          '& .MuiDialog-paper': {
            maxWidth: '70vw',
            width: '70vw', 
            maxHeight: '70vh',
            height : '70vh',
            borderRadius: '3px',
            margin: '0 auto',
            padding: "0 20px",
          }
        }}
      >
        <DialogTitle style={{ 
          padding: '16px 20px',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          fontSize: '18px',
          fontWeight: 400
        }}>
          <div style={{color:"#1976d2"}}>Define Custom Metric</div>
          <IconButton
            onClick={handleClose}
            style={{ padding: '8px' }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
  
        <div style={{ 
          borderBottom: '1px solid #e0e0e0',
          backgroundColor: 'white'
        }}>
          <Tabs 
            value={currentTab} 
            onChange={handleTabChange}
            aria-label="metric definition tabs"
            variant="standard"
            style={{ minHeight: '40px' }}
            TabIndicatorProps={{
              style: {
                backgroundColor: '#1976d2',
                height: 2
              }
            }}
            sx={{
              backgroundColor: 'white',
              '& .MuiTabs-flexContainer': {
                justifyContent: 'flex-start'
              },
              '& .MuiTab-root': {
                textTransform: 'none',
                fontSize: '14px',
                fontWeight: 400,
                minHeight: '40px',
                padding: '8px 80px',
                color: '#737373',
                border: 'none',
                borderRadius: 0,
                backgroundColor: 'white',
                maxWidth: '200px',
                width: '150px',
                '&:hover': {
                  color: '#1976d2'
                }
              },
              '& .MuiTab-root.Mui-selected': {
                color: '#1976d2 !important',
                fontWeight: 500,
                backgroundColor: 'white'
              },
              '& .MuiTabs-indicator': {
                display: 'block',
                backgroundColor: '#1976d2', 
                height: '2px'
              }
            }}
          >
            <Tab label="Define Variables" id="metric-tab-0" style={{color: currentTab === 0 ? '#1976d2' : '#737373'}} />
            <Tab label="Process Data" id="metric-tab-1" style={{color: currentTab === 1 ? '#1976d2' : '#737373'}} />
          </Tabs>
        </div>
        
        {alertMessage && (
          <Alert 
            severity="warning" 
            style={{ margin: '10px 15px 0', fontSize: '14px' }}
            onClose={() => setAlertMessage(null)}
          >
            {alertMessage}
          </Alert>
        )}
        
        <DialogContent style={dialogContentStyle}>
          {/* Define Variables Tab */}
          {currentTab === 0 && (
            <div key="variables-tab">
              <div style={{ display: 'flex', gap: '20px', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ color:"#1976d2", display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>Select Dataset</label>
                  <Select
                    options={groupedOptions}
                    value={selectedDatasetOption}
                    onChange={handleDataSetChangeOnDropdown}
                    placeholder="Search in dataset..."
                    styles={{
                      control: (base) => ({ 
                        ...base, 
                        minHeight: '36px',
                        boxShadow: 'none',
                        borderColor: '#ccc' 
                      })
                    }}
                  />
                </div>
                
                {current_dataset && (
                  <div style={{ flex: 1 }}>
                    <label style={{ color:"#1976d2", display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>Select Attribute</label>
                    <Select
                      options={attributes_list.names.map((name, index) => ({
                        value: name,
                        label: attributes_list.titles[index] || name
                      }))}
                      value={current_attribute ? {
                        value: current_attribute,
                        label: attributes_list.titles[attributes_list.names.indexOf(current_attribute)]
                      } : null}
                      onChange={handleAttributeChangeOnDropdown}
                      placeholder="Select attribute..."
                      styles={{
                        control: (base) => ({ 
                          ...base, 
                          minHeight: '36px',
                          boxShadow: 'none',
                          borderColor: '#ccc' 
                        })
                      }}
                    />
                  </div>
                )}
              </div>

              {/* Parameters */}
              {current_attribute && Object.keys(modal_current_parameters).length > 0 && (
                <div style={{ marginBottom: '20px' }}>
                  {/*<label style={{ display: 'block', marginBottom: '8px', fontWeight: '500' }}>Parameters</label>*/}
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                    {Object.keys(modal_current_parameters).map((key, paramIndex) => {
                      const { type, titles, codes, current_code } = modal_current_parameters[key as keyof typeof modal_current_parameters];
                      const title = key.split("_")[0];
                      return (
                        <IsolatedParameter key={key}>
                          <DynamicSelector
                            title={title}
                            type={type}
                            values={codes}
                            labels={titles}
                            name={`dialog_${key}_${Math.random().toString(36).substring(2, 9)}`} // Add a random suffix
                            selectedValue={current_code}
                            onChange={(value) => {
                              // Immediate state update
                              setTimeout(() => handleDynamicSelectorChange(key, value), 0);
                            }}
                          />
                        </IsolatedParameter>
                      );
                    })}
                  </div>
                </div>
              )}
          
              {/* Year slider */}
              {current_attribute && has_time && current_year ? (
                  <YearSelectSlider 
                    key={current_year} 
                    value={current_year} 
                    handleYearSliderChange={handleYearSliderChange} 
                    years={valid_years}/>
              ) : null}


              {(current_attribute && has_time && (current_month !== undefined && !isNaN(current_month))  && valid_months.length > 1 && 
              <div style={{ marginBottom: '20px' }}>
              <MonthSelectSlider key={`MonthSelectSlider_${current_month}`} value={current_month} handleMonthSliderChange={handleMonthSliderChange} months={valid_months} />
              </div>)
              }
                        

  
              <div style={{ display: 'flex', gap: '15px', alignItems: 'flex-end', marginBottom: '20px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ color:"#1976d2", display: 'block', marginBottom: '8px', fontWeight: 500, fontSize: '14px' }}>Parameter Name</label>
                  <TextField
                    value={current_variable_name}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder="Enter parameter name"
                    variant="outlined"
                    size="small"
                    fullWidth
                    style={{ fontSize: '14px' }}
                    InputProps={{ 
                      style: { height: '36px' }
                    }}
                  />
                </div>
                <Button 
                  onClick={addNewParameter}
                  variant="contained"
                  style={{ 
                    backgroundColor: '#1976d2', 
                    height: '36px',
                    textTransform: 'none',
                    boxShadow: 'none',
                    fontSize: '14px',
                  }}
                  disabled={data_is_missing || !((current_variable_name && current_dataset && current_attribute) || (is_current_user_defined && current_variable_name))}
                >
                  Define Parameter
                </Button>
              </div>
              
              <div style={{ marginTop: '25px' }}>
                <h3 style={{ 
                  margin: '0 0 12px 0', 
                  fontSize: '16px', 
                  fontWeight: 500 ,
                  color:"#1976d2"
                }}>Defined Parameters</h3>
                <ParametersSelected 
                  ComparitorDatas={ComparitorDatas} 
                  setComparitorDatas={setComparitorDatas}
                />
              </div>
            </div>
          )}
  
          {/* Process Data Tab */}
          {currentTab === 1 && (
            <div >
              {/* Blue Info Bar */}
              <div style={{
                backgroundColor: '#e3f2fd',
                padding: '12px 15px',
                borderRadius: '4px',
                marginBottom: '20px',
                fontSize: '14px',
                color: '#0d47a1'
              }}>
                <span>Available Parameters: {Object.keys(ComparitorDatas.variables).join(', ')}</span>
              </div>
              
              <div style={{ 
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                gap: '10px',
                marginBottom: '20px',
              }}>
                {["Fresh Surface Water", "Fresh Groundwater", "Brackish Groundwater"].map((title, index) => (
                  <div key={`${index}_${equations[index]}`} style={processDataCardStyle}>
                    <h3 style={{ margin: '0 0 10px 0', fontSize: '16px', color: '#1976d2' }}>{title}</h3>
                    <div style={{ marginBottom: '12px' }}>
                    <TextField
                      placeholder="Enter equation"
                      value={equations[index]}
                      onChange={(e) => {
                        // Store the active element and selection position before state update
                        const activeElement = document.activeElement;
                        const isThisFieldActive = activeElement === e.target;
                        const selectionStart = isThisFieldActive && 'selectionStart' in e.target ? 
                          e.target.selectionStart : null;
                        const selectionEnd = isThisFieldActive && 'selectionEnd' in e.target ? 
                          e.target.selectionEnd : null;
                        
                        // Call the update function
                        handleEquationChange(index, e.target.value);
                        
                        // If this field was active, schedule focus restoration
                        if (isThisFieldActive) {
                          // Use requestAnimationFrame to wait for render
                          requestAnimationFrame(() => {
                            if (inputRefs.current[index]) {
                              const input = inputRefs.current[index];
                              // Focus the element
                              input?.focus();
                              
                              // Restore cursor position if possible
                              if (input && selectionStart !== null && selectionEnd !== null && 
                                  'setSelectionRange' in input) {
                                input.setSelectionRange(selectionStart, selectionEnd);
                              }
                            }
                          });
                        }
                      }}
                      fullWidth
                      variant="outlined"
                      size="small"
                      style={{ fontSize: '14px' }}
                      // Store ref to the input element
                      InputProps={{ 
                        style: { height: '36px' },
                        inputRef: (el) => {
                          inputRefs.current[index] = el;
                        }
                      }}
                    />
                    </div>
                    
                    <Button 
                      onClick={() => handleProcessData(index, true)} 
                      variant="contained"
                      style={{
                        backgroundColor: '#1976d2',
                        height: '36px',
                        marginBottom: '15px',
                        textTransform: 'none',
                        boxShadow: 'none',
                        fontSize: '14px'
                      }}
                      disabled={!equations[index] || Object.keys(ComparitorDatas.variables).length === 0}
                    >
                      Process Data
                    </Button>
                    
                    {loadedIndices.includes(index) && (
                      <div style={{ marginTop: '15px' }}>
                        <BarHistogram 
                          histogramData={histogramData[index]} 
                          sliderValues={sliderValues[index]}
                          setSliderValues={(newValues) => handleSliderChange(index, newValues)}  
                          isFlipped={isFlipped[index]}
                          setIsFlipped={() => handleReverseCategories(index)}
                        />
                        
                        {histogramData[index].type === "Discrete" && (
                          <div>
                            <CategoryAssignment 
                              categories={Object.fromEntries(
                                histogramData[index].categories.map((category, i) => [category, i])
                              )}
                              setBucketValues={setBucketValues}
                              bucketValues={bucketValues}
                              indexWaterType={index}
                            />
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </DialogContent>
        
        <DialogActions style={{
          padding: '12px 20px',
          borderTop: '1px solid #e0e0e0#',
          display: 'flex',
          justifyContent: 'space-between'
        }}>
          <Button 
            onClick={handleClose}
            style={{
              color: '#555',
              textTransform: 'none',
              fontSize: '14px'
            }}
          >
            Cancel
          </Button>
          
          <TextField
            placeholder="Custom metric name (required)"
            value={custom_metric_name}
            onChange={(e) => handleCustomNameChange(e.target.value)}
            size="small"
            style={{ width: '250px' }}
            InputProps={{ style: { height: '36px', fontSize: '14px' } }}
          />
          
          <Button 
            onClick={updateCustomMetadataonSave}
            variant="contained"
            style={{ 
              backgroundColor: '#1976d2',
              textTransform: 'none',
              boxShadow: 'none',
              fontSize: '14px'
            }}
            disabled={!custom_metric_name || Object.keys(ComparitorDatas.variables).length === 0 || loadedIndices.length === 0}
          >
            Save Custom Metric
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default UserDefinedMetric;